import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo
} from "react";
import { getAuth } from "firebase/auth";
import { getAvailableCredits } from "../api/creditApi";
import { guardedFirebaseRequest } from "../helpers/firebase_helpers";

interface CreditsContextType {
  availableCredits: number | null;
  isLoading: boolean;
  error: Error | null;
  refreshCredits: () => Promise<number>;
}

const CreditsContext = createContext<CreditsContextType | null>(null);

export const CreditsProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [availableCredits, setAvailableCredits] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refreshCredits = useCallback(async () => {
    try {
      setIsLoading(true);
      const credits = await getAvailableCredits();
      setAvailableCredits(credits);
      return credits;
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch credits")
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const auth = getAuth();
    if (auth.currentUser) {
      refreshCredits();
    }
  }, [refreshCredits]);

  const value = useMemo(
    () => ({
      availableCredits,
      isLoading,
      error,
      refreshCredits
    }),
    [availableCredits, isLoading, error, refreshCredits]
  );

  return (
    <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>
  );
};

export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error("useCredits must be used within a CreditsProvider");
  }
  return context;
};
